import React, { useEffect, useRef } from 'react';

/**
 * AriaLiveRegion - renders an invisible region that announces messages to screen readers
 * 
 * Usage:
 *   <AriaLiveRegion message={errorMessage} priority="assertive" />
 *   <AriaLiveRegion message={successMessage} priority="polite" />
 */
const AriaLiveRegion = ({
  message,
  priority = 'polite', // 'polite' | 'assertive'
  atomic = true,
  className = ''
}) => {
  const regionRef = useRef(null);
  const prevMessageRef = useRef('');

  useEffect(() => {
    if (!regionRef?.current || !message) return;
    // Only announce if message changed
    if (message === prevMessageRef?.current) return;
    prevMessageRef.current = message;

    // Clear then re-set to force re-announcement
    regionRef.current.textContent = '';
    const raf = requestAnimationFrame(() => {
      if (regionRef?.current) {
        regionRef.current.textContent = message;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [message]);

  return (
    <div
      ref={regionRef}
      role={priority === 'assertive' ? 'alert' : 'status'}
      aria-live={priority}
      aria-atomic={atomic ? 'true' : 'false'}
      className={`sr-only ${className}`}
    />
  );
};

/**
 * GlobalAriaAnnouncer - place once at app root to handle all announcements
 */
export const GlobalAriaAnnouncer = () => (
  <>
    <div
      id="sr-polite-region"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
    <div
      id="sr-assertive-region"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className="sr-only"
    />
  </>
);

export default AriaLiveRegion;
